/**
 * SCQ funnel shared helpers — storage, quiz-state contract, URL fallback, tracking.
 *
 * Storage keys and URL params are byte-identical to the headless funnel (kk-headless /scq)
 * so the two implementations can interoperate and dashboards compare like-for-like.
 * Every storage access is wrapped in try/catch: iOS private mode and some Android WebViews
 * throw on access, and a thrown quiz must never strand the customer.
 */
(function () {
  if (window.ScqStorage) return;

  var KEYS = {
    funnelId: 'cc_funnelId', // session
    answers: 'mw_quiz_answers', // local — JSON {'1'..'7': value}
    primaryType: 'mw_selectedType', // local
    secondaryType: 'secondaryType', // local
    routineUnlocked: 'scq_routine_unlocked', // local — 'true'
    systemQty: 'order4_system_qty', // session — '1' | '2' | '3'
    mainBundle: 'mainBundleProducts', // local — headless CartItem[] (analytics parity only)
    additional: 'additionalProducts', // local — always []
    orderBump: 'recommendedPopulateOrderBump', // local — always null
    utm: 'cc_utm_params', // session
    affiliate: 'cc_affiliate_params', // session
    ttclid: 'cc_ttclid', // session
  };

  var FUNNEL_ID = 'scq-quiz';
  var VALID_TYPES = ['collagen-depletion', 'oxidation-damage', 'barrier-depletion', 'texture-irregularity'];

  function store(kind) {
    try {
      return kind === 'session' ? window.sessionStorage : window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function safeGet(key, kind) {
    try {
      var s = store(kind);
      return s ? s.getItem(key) : null;
    } catch (e) {
      return null;
    }
  }

  function safeSet(key, value, kind) {
    try {
      var s = store(kind);
      if (!s) return false;
      s.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  }

  function safeRemove(key, kind) {
    try {
      var s = store(kind);
      if (s) s.removeItem(key);
    } catch (e) {
      /* noop */
    }
  }

  function safeJson(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /** True when localStorage round-trips a value; false when blocked. */
  function storageAvailable() {
    var probe = '__scq_probe__';
    if (!safeSet(probe, '1', 'local')) return false;
    var ok = safeGet(probe, 'local') === '1';
    safeRemove(probe, 'local');
    return ok;
  }

  function isValidType(t) {
    return typeof t === 'string' && VALID_TYPES.indexOf(t) !== -1;
  }

  /** Quiz state from storage: { primary, secondary, answers } (fields may be null/{}). */
  function readQuizStateFromStorage() {
    var primary = safeGet(KEYS.primaryType, 'local');
    var secondary = safeGet(KEYS.secondaryType, 'local');
    var answers = safeJson(safeGet(KEYS.answers, 'local')) || {};
    return {
      primary: isValidType(primary) ? primary : null,
      secondary: isValidType(secondary) ? secondary : null,
      answers: typeof answers === 'object' && answers !== null ? answers : {},
    };
  }

  /** Quiz state from the URL fallback ?st=&st2=&a1..a7 (storage-blocked path). */
  function quizStateFromQuery(search) {
    var params = new URLSearchParams(search === undefined ? window.location.search : search);
    var st = params.get('st');
    var st2 = params.get('st2');
    var answers = {};
    for (var i = 1; i <= 7; i++) {
      var v = params.get('a' + i);
      if (v) answers[String(i)] = v;
    }
    return {
      primary: isValidType(st) ? st : null,
      secondary: isValidType(st2) ? st2 : null,
      answers: answers,
    };
  }

  /** Storage wins when present; URL fills gaps. */
  function readQuizState() {
    var fromStorage = readQuizStateFromStorage();
    if (fromStorage.primary) return fromStorage;
    var fromUrl = quizStateFromQuery();
    return {
      primary: fromUrl.primary,
      secondary: fromUrl.secondary,
      answers: Object.keys(fromUrl.answers).length ? fromUrl.answers : fromStorage.answers,
    };
  }

  /** Persist quiz state (primary, optional secondary, answers). Returns true if storage took it. */
  function writeQuizState(state) {
    var ok = safeSet(KEYS.primaryType, state.primary, 'local');
    if (state.secondary) safeSet(KEYS.secondaryType, state.secondary, 'local');
    else safeRemove(KEYS.secondaryType, 'local');
    safeSet(KEYS.answers, JSON.stringify(state.answers || {}), 'local');
    safeSet(KEYS.funnelId, FUNNEL_ID, 'session');
    return ok && safeGet(KEYS.primaryType, 'local') === state.primary;
  }

  /** Build the ?st=&st2=&a1..a7 query for a URL (preserving existing params on that URL). */
  function appendQuizStateToUrl(url, state) {
    if (!state || !state.primary) return url;
    var u;
    try {
      u = new URL(url, window.location.origin);
    } catch (e) {
      return url;
    }
    u.searchParams.set('st', state.primary);
    if (state.secondary) u.searchParams.set('st2', state.secondary);
    var answers = state.answers || {};
    for (var i = 1; i <= 7; i++) {
      if (answers[String(i)]) u.searchParams.set('a' + i, answers[String(i)]);
    }
    return u.pathname + u.search + u.hash;
  }

  /** Capture UTM / Everflow / TikTok click params into session storage (mirrors headless mount). */
  function captureAttributionParams() {
    var params = new URLSearchParams(window.location.search);
    var utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
      if (params.get(k)) utm[k] = params.get(k);
    });
    if (Object.keys(utm).length) safeSet(KEYS.utm, JSON.stringify(utm), 'session');
    var aff = {};
    ['_ef_transaction_id', 'oid', 'affid', 'sub1', 'sub2', 'sub3', 'sub4', 'sub5'].forEach(function (k) {
      if (params.get(k)) aff[k] = params.get(k);
    });
    if (Object.keys(aff).length) safeSet(KEYS.affiliate, JSON.stringify(aff), 'session');
    if (params.get('ttclid')) safeSet(KEYS.ttclid, params.get('ttclid'), 'session');
    safeSet(KEYS.funnelId, FUNNEL_ID, 'session');
  }

  /** PostHog capture with the funnel tag; silent when PostHog isn't loaded (UK store). */
  function track(event, props) {
    try {
      if (!window.posthog || typeof window.posthog.capture !== 'function') return;
      var payload = Object.assign({ funnel: 'scq' }, props || {});
      window.posthog.capture(event, payload);
    } catch (e) {
      /* never let analytics break the funnel */
    }
  }

  /** Push an event onto Elevar's data layer if the embed is present. */
  function elevarPush(event) {
    try {
      window.ElevarDataLayer = window.ElevarDataLayer || [];
      window.ElevarDataLayer.push(event);
    } catch (e) {
      /* noop */
    }
  }

  window.ScqStorage = {
    KEYS: KEYS,
    FUNNEL_ID: FUNNEL_ID,
    VALID_TYPES: VALID_TYPES,
    safeGet: safeGet,
    safeSet: safeSet,
    safeRemove: safeRemove,
    safeJson: safeJson,
    storageAvailable: storageAvailable,
    isValidType: isValidType,
    readQuizState: readQuizState,
    readQuizStateFromStorage: readQuizStateFromStorage,
    quizStateFromQuery: quizStateFromQuery,
    writeQuizState: writeQuizState,
    appendQuizStateToUrl: appendQuizStateToUrl,
    captureAttributionParams: captureAttributionParams,
    track: track,
    elevarPush: elevarPush,
  };
})();
